import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { resolveClientRefDisplayColor } from "@app/_helpers/client-color";
import { toPromise } from "@app/_helpers/promise";
import { createRxValue } from "@app/_helpers/utils";
import { UtilService } from "@app/_services/util.service";
import { AppService } from "@app/app.service";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import RoundingConfig from "@app/shared/dialogs/rounding-dialog/models/rounding-config";
import { RoundingDialogComponent, RoundingTypes } from "@app/shared/dialogs/rounding-dialog/rounding-dialog.component";
import {
	nearestFutureMinutes,
	nearestMinutes,
	nearestPastMinutes,
} from "@app/shared/dialogs/rounding-dialog/rounding-functions";
import { TimeTrackerCalendarUpdateDialogComponent } from "@app/shared/time-tracker-calendar-update-dialog/time-tracker-calendar-update-dialog.component";
import { environment } from "@env/environment";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	ClientsQuery,
	Logger,
	MyTimesService,
	ProjectsQuery,
	Task,
	Time,
	TimesService,
	UserSettingsQuery,
} from "@timeghost/web-api";
import { CalendarEvent } from "calendar-utils";
import { chain, sumBy } from "lodash";
import { BehaviorSubject, combineLatest } from "rxjs";
import { distinctUntilChanged, filter, map, startWith, switchMap } from "rxjs/operators";
import {
	DataTableFilterData,
	DataTableFilterDialogComponent,
} from "./data-table-filter-dialog/data-table-filter-dialog.component";
export enum GroupType {
	Billable = "billable",
	Project = "project",
	User = "user",
	Client = "client",
	Task = "task",
}

@UntilDestroy()
@Component({
	selector: "app-data-table",
	templateUrl: "./data-table.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ["./data-table.component.scss"],
})
export class DataTableComponent implements OnInit {
	@Input()
	ignoreGroups: string[] = [];
	readonly groupTypes: typeof GroupType = GroupType;
	private selectedGroup = new BehaviorSubject<GroupType>(GroupType[this.allowedGroups[0]]);
	get group(): GroupType {
		return this.selectedGroup.getValue();
	}
	get allowedGroups() {
		return Object.keys(this.groupTypes).filter(
			(x) => this.ignoreGroups.findIndex((g) => g.toLowerCase() === x.toLowerCase()) === -1
		);
	}
	@Output()
	readonly groupChange = new EventEmitter(true);
	@Input()
	set group(val: GroupType) {
		this.selectedGroup.next(val);
		this.groupChange.emit(val);
	}
	@Output()
	readonly group$ = this.selectedGroup.asObservable().pipe(
		startWith(this.group),
		filter((x) => x !== undefined),
		distinctUntilChanged()
	);
	readonly group$name = this.group$.pipe(map((x) => GroupType[x]));
	@Input()
	currencyCode: string;

	@Input()
	showParentData: GroupType[] = null;

	@Input()
	showRoundingButton: boolean = true;

	@Input()
	nameClickOpensEdit = false;

	hasShowParentFlag(type: GroupType) {
		if (!this.showParentData) return true;
		return this.showParentData.includes(type);
	}

	private tasks = createRxValue<Task[]>([]);
	private tasksLoading: Promise<void> | null = null;
	private entries = createRxValue<any[]>([]);
	readonly entries$ = combineLatest([
		this.group$.pipe(startWith(GroupType[this.allowedGroups[0]])),
		this.entries.asObservable(),
		this.appService.roundingData$.pipe(startWith(this.appService.roundingData)),
		this.tasks.asObservable(),
	]).pipe(
		filter(([grp, entries]) => !!grp && !!entries),
		distinctUntilChanged(),
		map(([grp, x, rounding, tasks]) => {
			if (x.find((y) => y.cosmosEntityName === "times")) {
				x = chain(x)
					.orderBy((y: Time) => new Date(y.start))
					.groupBy((y: Time) => {
						// @ts-ignore
						if (grp === GroupType.Client && y.client) {
							// @ts-ignore
							return y.client.id;
						}
						if (grp === GroupType.Project && !!y.project.id) {
							return y.project.id;
						}
						if (grp === GroupType.User && !!y.user.id) {
							return y.user.id;
						}
						if (grp === GroupType.Task && !!y.task?.id) {
							return y.task.id;
						}
						// Group Times Without Task by their projects
						if (grp === GroupType.Task && !y.task?.id) {
							if (!!y.project.id) {
								return y.project.id;
							}
						}
						if (grp === GroupType.Billable && !y.billable) {
							return y.billable;
						}
						return null;
					})
					.toPairs()
					.value()
					.map(([key, value]: [any, Time[]]) => {
						const data = value.length > 0 ? value[0] : null;
						return {
							key,
							data,
							// @ts-ignore
							type: value.length > 0 ? value[0].cosmosEntityName : null,
							group: grp,
							sum: () => {
								let sum = value.length > 0 ? sumBy(value, (s) => s.timeDiff) * 1000 || 0 : 0;
								if (rounding?.enabled === true) {
									if (rounding.type === RoundingTypes.Nearest) {
										sum = nearestMinutes(rounding.minutes, sum) * 60 * 1000;
									}
									if (rounding.type === RoundingTypes.UpTo) {
										sum = nearestFutureMinutes(rounding.minutes, sum) * 60 * 1000;
									}
									if (rounding.type === RoundingTypes.DownTo) {
										sum = nearestPastMinutes(rounding.minutes, sum) * 60 * 1000;
									}
								}
								return UtilService.parseMS(sum, {
									showSeconds: false,
								});
							},
							value,
						};
					});
			}
			return { values: x, tasks };
		}),
		map(({ values, tasks }) => {
			if (!values.length) return [];
			const groupedBy = values[0].group;
			if (groupedBy == GroupType.Project) {
				const porjects = this.projectQuery.getAll();
				return values.map((v) => {
					if (!v.key) return v;
					const project = porjects.find((p) => p.id == v.key);
					if (!project)
						return {
							...v,
							estStat: { estimation: 0, timesRecorded: 0, completed: false, billable: false },
						};
					return {
						...v,
						data: { ...v.data, project: project },
						estStat: {
							estimation: project.estimation,
							timesRecorded: project.timesRecorded,
							completed: project.completed,
							billable: project.billable,
						},
					};
				});
			} else if (groupedBy == GroupType.Task) {
				const valuesWithTasks = values.map((v) => {
					if (!v.key) return v;
					const task: Task = tasks.find((t) => t.id == v.key);
					if (!task)
						return {
							...v,
							estStat: { estimation: 0, timesRecorded: 0, completed: false, billable: false },
						};
					const project = task.project?.id ? this.projectQuery.getEntity(task.project.id) : null;
					return {
						...v,
						taskName: task.name || "",
						estStat: {
							estimation: task.estimation,
							timesRecorded: task.timesRecorded,
							completed: task.completed,
							billable: !!project?.billable,
						},
					};
				});
				// Sort by task name alphabetically
				return valuesWithTasks
					.sort((a, b) =>
						(a.taskName || "").localeCompare(b.taskName || "", undefined, { sensitivity: "base" })
					)
					.map(({ taskName, ...rest }) => rest);
			}
			return values;
		})
	);
	@Input()
	showColors: boolean = false;
	@Input()
	set data(val: any) {
		this.entries.next(val);
	}
	constructor(
		private dialog: MatDialog,
		private userSettingsQuery: UserSettingsQuery,
		private appService: AppService,
		private clientQuery: ClientsQuery,
		private projectQuery: ProjectsQuery,
		private timeService: MyTimesService,
		private timesService: TimesService,
		private router: Router,
		private route: ActivatedRoute,
		private deletionService: DeletionUndoService
	) {}
	isDefaultClient(id: string) {
		return this.clientQuery.getEntity(id)?.useAsDefault === true;
	}
	isDefaultProject(id: string) {
		return this.projectQuery.getEntity(id)?.useAsDefault === true;
	}
	getEntityColor(id: string) {
		const prefix = this.selectedGroup.value?.toString().toLowerCase();
		if (prefix) id = prefix + "." + id;
		return this.appService.getColorById(id);
	}
	getProjectColor(id: string) {
		return this.projectQuery.getEntity(id)?.color;
	}
	clientDisplayColor(client: { id?: string; color?: string | null } | null | undefined) {
		return resolveClientRefDisplayColor(
			client,
			(id) => this.clientQuery.getEntity(id),
			(n) => this.appService.colorScheme.getColorFromScheme(n)
		);
	}
	get HFormat() {
		return this.appService.timeFormat;
	}
	roundingData$ = this.appService.roundingData$;
	get roundingData() {
		return this.appService.roundingData;
	}
	get hasRounding() {
		return this.roundingData?.enabled === true;
	}
	openRoundingDialog() {
		this.dialog
			.open(RoundingDialogComponent, {
				data: new RoundingConfig({
					data: this.roundingData,
				}),
			})
			.afterClosed()
			.pipe(
				switchMap(async (x) => {
					if (!!x) {
						await this.appService.updateRounding(x);
					}
					log.debug(x);
					return x;
				})
			);
	}
	applyGroup(grp: string) {
		this.group = GroupType[grp] || GroupType[this.allowedGroups[0]];
	}
	get timeZone() {
		return this.userSettingsQuery.getValue().settings.timeZone;
	}
	ngOnInit() {
		void this.loadTasks();

		this.deletionService.deleted
			.pipe(
				filter((ev) => ev.type === "timeEntry"),
				untilDestroyed(this)
			)
			.subscribe((ev) => {
				const time = this.entries.value?.find((x: Time) => x.id === ev.key);
				if (!time) return;
				this.deletedTime.emit(time);
				this.entries.update((oldValues) => oldValues.filter((t) => t.id !== ev.key), true);
			});

		this.deletionService.restored
			.pipe(
				filter((ev) => ev.type === "timeEntry"),
				untilDestroyed(this)
			)
			.subscribe(() => {
				this.restoredTime.emit();
			});
	}
	private loadTasks(): Promise<void> {
		if (this.tasks.value?.length) return Promise.resolve();
		if (this.tasksLoading) return this.tasksLoading;
		this.tasksLoading = toPromise(this.timesService.adapter.get<Task[]>(environment.serverUrl + "/get/tasks")).then(
			(x) => this.tasks.update(x ?? [])
		);
		return this.tasksLoading;
	}
	getRecordedTime(estStat: {
		timesRecorded?: { billable?: number; notBillable?: number };
		billable?: boolean;
	}): number {
		if (!estStat?.timesRecorded) return 0;
		const isBillableProject = !!estStat.billable;
		return (
			(estStat.timesRecorded.billable?.clamp(0, estStat.timesRecorded.billable) || 0) +
			(!isBillableProject
				? estStat.timesRecorded.notBillable?.clamp(0, estStat.timesRecorded.notBillable) || 0
				: 0)
		);
	}
	trackItem(index: number, item: any) {
		return item.key || index;
	}
	trackItemEntry(index: number, item: Time) {
		return item.id || index;
	}
	groupTypeComparer(left: any, right: any) {
		return (
			left == right || left == GroupType[right] || right == GroupType[left] || GroupType[left] == GroupType[right]
		);
	}
	openFilterDialog() {
		return this.dialog
			.open(DataTableFilterDialogComponent, {
				data: <Partial<DataTableFilterData>>{
					group: this.group,
					ignoredGroups: this.ignoreGroups,
				},
			})
			.afterClosed()
			.pipe(filter((x) => !!x))
			.subscribe((x) => {
				if (x.group) this.group = x.group;
			});
	}

	@Output()
	deletedTime = new EventEmitter(true);
	@Output()
	restoredTime = new EventEmitter<void>(true);
	@Output()
	updatedTime = new EventEmitter(true);
	deleteTime(id: string) {
		const time = this.entries.value?.find((x: Time) => x.id === id);
		if (!time) return;
		this.deletionService.deleteWithUndo({ type: "timeEntry", entry: time });
	}
	editTime(id: string) {
		const time: Time = this.entries.value?.find((x: Time) => x.id === id);
		if (!time) return;
		return this.dialog
			.open(TimeTrackerCalendarUpdateDialogComponent, {
				data: <CalendarEvent>{
					title: time.name,
					start: new Date(time.start),
					end: new Date(time.end),
					meta: { time },
				},
			})
			.afterClosed()
			.subscribe(([t]: [Time]) => {
				if (t) this.updatedTime.emit(t);
			});
	}
	@Output()
	namePress = new EventEmitter();
	onEntryClick(group: GroupType, entity: Time) {
		if (this.nameClickOpensEdit) {
			if (entity.currentUserCanEdit) this.editTime(entity.id);
			return;
		}
		this.onNameClick(group, entity);
	}
	onNameClick(group: GroupType, entity: Time) {
		return this.namePress.emit({
			group,
			entity,
		});
	}
}
const log = new Logger(DataTableComponent.name);
